import { Icon } from '@studio/design-system/icon';
import type { KeyboardEvent, ReactElement } from 'react';
import { useEffect, useId, useRef } from 'react';
import { Sel, STagNew } from '#~/stag/index.js';

export interface MultiBridgeModalProps {
  readonly studioName: string;
  readonly machineNames: readonly string[];
  readonly busy: boolean;
  readonly onCancel: (reason: 'cancel' | 'backdrop' | 'escape') => void;
  readonly onConfirm: () => void;
}

/**
 * Signed-off occupied-Studio consent. It describes an additive relationship
 * and never reuses replacement copy, so confirmation cannot imply invalidation.
 */
export const MultiBridgeModal = ({
  studioName,
  machineNames,
  busy,
  onCancel,
  onConfirm
}: MultiBridgeModalProps): ReactElement => {
  const modalRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const idSeed = useId().replaceAll(':', '');
  const titleId = `multibridge-title-${idSeed}`;
  const descriptionId = `multibridge-description-${idSeed}`;
  const bridgeCount = machineNames.length;
  const converting = bridgeCount === 1;

  useEffect(() => {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modalRef.current?.focus();
    return () => returnFocusRef.current?.focus();
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!busy) onCancel('escape');
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [
      ...(modalRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled)') ?? [])
    ];
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-dark/[0.72] p-24 backdrop-blur-[3px]'
      {...STagNew(Sel.StudioPicker, 'multibridge_backdrop')}
      onClick={(event): void => {
        if (event.target === event.currentTarget && !busy) onCancel('backdrop');
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={modalRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={busy}
        tabIndex={-1}
        className='w-full max-w-[460px] rounded-12 bg-base-100 p-24 text-center text-light shadow-[0_16px_48px_rgba(0,0,0,0.6),0_2px_8px_rgba(0,0,0,0.5),inset_0_0_0_1px_rgba(255,255,255,0.06)] focus:outline-none'
        {...STagNew(Sel.StudioPicker, 'multibridge_modal')}
      >
        {/* See knowledge: design-system-tailwind-color-tokens (ai.knowledge/studio.client) — the
            opacity scale has no /20 step, so bg-accent/20 emitted no CSS. */}
        <span className='mx-auto mb-[14px] flex size-40 items-center justify-center rounded-full bg-accent/[0.2]'>
          <Icon icon='bridge' aria-hidden='true' className='text-[20px] text-accent' />
        </span>
        <h2 id={titleId} className='text-[20px] font-demibold leading-[1.2] tracking-[-0.01em]'>
          Add your bridge to {studioName}?
        </h2>
        <div id={descriptionId} className='mt-8 text-[13px] leading-[1.55] text-muted'>
          {converting ? (
            <>
              This studio is linked to {machineNames[0] ?? 'another bridge'}. Adding your bridge
              will make it a multibridge studio.
            </>
          ) : (
            <>
              This is a multibridge studio with {bridgeCount} bridges. Adding yours brings it to{' '}
              {bridgeCount + 1}.
              {bridgeCount > 0 && (
                <div className='mt-14 rounded-6 bg-base-200 p-12 text-left'>
                  <p className='mb-6 text-[10px] font-demibold uppercase tracking-[0.08em] text-secondary'>
                    Already linked
                  </p>
                  {machineNames.map((name, index) => (
                    <p key={`${name}-${index}`} className='text-[12px] leading-[1.5] text-muted'>
                      {name}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className='mt-20 flex justify-center gap-10'>
          <button
            type='button'
            disabled={busy}
            className='h-[32px] rounded-6 border border-white/10 bg-base-200 px-[14px] text-[13px] font-medium text-light hover:bg-base-50 focus-visible:outline-none focus-visible:ring focus-visible:ring-focus/50 disabled:opacity-50'
            {...STagNew(Sel.StudioPicker, 'multibridge_cancel')}
            onClick={(): void => onCancel('cancel')}
          >
            Cancel
          </button>
          <button
            type='button'
            disabled={busy}
            className='h-[32px] rounded-6 bg-primary px-[14px] text-[13px] font-demibold text-white hover:bg-primary-highlight focus-visible:outline-none focus-visible:ring focus-visible:ring-focus/50 disabled:opacity-50'
            {...STagNew(Sel.StudioPicker, 'multibridge_confirm')}
            onClick={onConfirm}
          >
            Add my bridge
          </button>
        </div>
      </div>
    </div>
  );
};
